import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, ShoppingCart, ChevronLeft, ChevronRight, Check, Info } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';
import { CustomerColorSizeSelector } from '../components/ColorSizePicker';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BrandProductPage = () => {
  const { productId } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/brand-products/${productId}`);
      setProduct(res.data);
    } catch (err) {
      console.error('Error fetching product:', err);
      setError('Product not found');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product.price_on_request) {
      toast.info('Please contact us for pricing on this item');
      return;
    }
    
    // Validate color/size selection if product has these options
    const hasColors = product.available_colors && product.available_colors.length > 0;
    const hasSizes = product.sizes && product.sizes.length > 0;
    
    if (hasColors && !selectedColor) {
      toast.error('Please select a color');
      return;
    }
    if (hasSizes && !selectedSize) {
      toast.error('Please select a size');
      return;
    }
    
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.images?.[0] || '/placeholder.jpg',
      quantity: quantity,
      type: 'brand_product',
      selectedColor: selectedColor,
      selectedSize: selectedSize
    });
    
    // Build toast message with selected options
    let message = `${product.name}`;
    if (selectedColor) message += ` (${selectedColor})`;
    if (selectedSize) message += ` - Size ${selectedSize}`;
    message += ' added to cart';
    
    toast.success(message);
  };

  const nextImage = () => {
    if (product?.images?.length > 1) {
      setSelectedImageIndex((prev) => 
        prev === product.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (product?.images?.length > 1) {
      setSelectedImageIndex((prev) => 
        prev === 0 ? product.images.length - 1 : prev - 1
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0EFEA]">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF6B35]" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F0EFEA]">
        <h1 className="text-2xl font-heading font-bold text-[#0A2342] mb-4">Product Not Found</h1>
        <Link to="/">
          <Button className="bg-[#FF6B35] text-white">Return Home</Button>
        </Link>
      </div>
    );
  }

  const images = product.images?.length > 0 
    ? product.images 
    : ['https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800'];

  return (
    <div className="min-h-screen bg-[#F0EFEA] py-12" data-testid="brand-product-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Back Link */}
        <Link 
          to={`/brand/${product.brand_slug || 'divevolk'}`}
          className="inline-flex items-center text-[#0A2342] hover:text-[#FF6B35] mb-8 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Products
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative aspect-square bg-white rounded-lg overflow-hidden shadow-lg flex items-center justify-center">
              <img 
                src={images[selectedImageIndex]}
                alt={product.name}
                className="w-full h-full object-contain"
              />
              
              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <button 
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-6 h-6 text-[#0A2342]" />
                  </button>
                  <button 
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-6 h-6 text-[#0A2342]" />
                  </button>
                </>
              )}

              {/* Badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                {product.featured && (
                  <Badge className="bg-[#FF6B35] text-white">Featured</Badge>
                )}
                {!product.in_stock && (
                  <Badge variant="outline" className="bg-white">Out of Stock</Badge>
                )}
              </div>
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImageIndex(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImageIndex === idx 
                        ? 'border-[#FF6B35]' 
                        : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img 
                      src={img} 
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              {product.category && (
                <Badge variant="outline" className="mb-2">{product.category}</Badge>
              )}
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#0A2342]">
                {product.name}
              </h1>
            </div>

            {/* Price */}
            <div className="py-4 border-y border-gray-200">
              {product.price_on_request ? (
                <div className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-[#FF6B35]" />
                  <span className="text-2xl font-semibold text-[#FF6B35]">Price on Request</span>
                </div>
              ) : (
                <span className="text-3xl font-mono-accent font-bold text-[#0A2342]">
                  R{product.price?.toLocaleString()}
                </span>
              )}
            </div>

            {/* Description */}
            <div>
              <h3 className="text-lg font-semibold text-[#0A2342] mb-2">Description</h3>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {product.description}
              </p>
            </div>

            {/* Specifications */}
            {product.specifications && (
              <div className="bg-white rounded-lg p-6 shadow">
                <h3 className="text-lg font-semibold text-[#0A2342] mb-3">Specifications</h3>
                <div className="text-gray-700 whitespace-pre-line">
                  {product.specifications}
                </div>
              </div>
            )}

            {/* Color and Size Selection */}
            {((product.available_colors && product.available_colors.length > 0) || 
              (product.sizes && product.sizes.length > 0)) && (
              <div className="bg-white rounded-lg p-6 shadow">
                <CustomerColorSizeSelector
                  colors={product.available_colors || []}
                  customColors={product.custom_colors || []}
                  sizes={product.sizes || []}
                  sizeType={product.size_type || 'letter'}
                  selectedColor={selectedColor}
                  selectedSize={selectedSize}
                  onColorChange={setSelectedColor}
                  onSizeChange={setSelectedSize}
                />
              </div>
            )}

            {/* Quantity & Add to Cart */}
            <div className="space-y-4">
              {!product.price_on_request && product.in_stock && (
                <div className="flex items-center gap-4">
                  <span className="text-gray-600">Quantity:</span>
                  <div className="flex items-center border rounded-lg">
                    <button 
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-4 py-2 hover:bg-gray-100 transition-colors"
                    >
                      -
                    </button>
                    <span className="px-4 py-2 border-x min-w-[60px] text-center">
                      {quantity}
                    </span>
                    <button 
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-4 py-2 hover:bg-gray-100 transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              <Button
                size="lg"
                onClick={handleAddToCart}
                disabled={!product.in_stock && !product.price_on_request}
                className={`w-full ${
                  product.price_on_request 
                    ? 'bg-[#0A2342] text-white hover:bg-[#0A2342]/90' 
                    : 'bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90'
                }`}
                data-testid="product-add-to-cart"
              >
                {!product.in_stock ? (
                  'Out of Stock'
                ) : product.price_on_request ? (
                  'Contact Us for Pricing'
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add to Cart - R{(product.price * quantity).toLocaleString()}
                  </>
                )}
              </Button>

              {/* Stock Status */}
              {product.in_stock && (
                <div className="flex items-center gap-2 text-green-600">
                  <Check className="w-5 h-5" />
                  <span>In Stock</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandProductPage;
